/**
 * Everything the app keeps in the browser beyond settings, in one IndexedDB
 * database. Nothing here ever leaves the device.
 *
 *  handles    file and folder handles the user granted, by id, so recent files,
 *             the last session and the open folder survive a reload
 *  snapshots  the text of files opened without a handle (browsers with no File
 *             System Access), so Open Recent can still reopen them, as copies
 *  journal    unsaved work, written as it is typed, offered back after a crash
 *  versions   each file's earlier contents, kept before a save replaces them
 *  themes     imported user themes (CSS text)
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { JournalEntry } from './types'

export interface HandleRecord {
  id: string
  kind: 'file' | 'directory'
  name: string
  handle: FileSystemFileHandle | FileSystemDirectoryHandle
  /** When it was last used, so old entries can be pruned. */
  usedAt: number
}

export interface SnapshotRecord {
  id: string
  name: string
  content: string
  encoding: 'utf8' | 'utf16le' | 'utf16be'
  hasBom: boolean
  eol: '\n' | '\r\n'
  /** The file's own time when it was read. */
  mtimeMs: number
  size: number
  usedAt: number
}

export interface JournalRecord extends JournalEntry {
  /** The tab that wrote it: see `recovery.ts`. */
  owner: string
}

export interface VersionRecord {
  /** `${pathKey}|${id}` */
  key: string
  pathKey: string
  path: string
  id: string
  savedAtMs: number
  size: number
  bytes: Uint8Array
}

export interface ThemeRecord {
  id: string
  name: string
  css: string
}

interface Schema extends DBSchema {
  handles: { key: string; value: HandleRecord }
  snapshots: { key: string; value: SnapshotRecord }
  journal: { key: string; value: JournalRecord }
  versions: { key: string; value: VersionRecord; indexes: { byPath: string } }
  themes: { key: string; value: ThemeRecord }
}

let dbPromise: Promise<IDBPDatabase<Schema>> | null = null

export function db(): Promise<IDBPDatabase<Schema>> {
  if (!dbPromise) {
    dbPromise = openDB<Schema>('ekram-md', 1, {
      upgrade(d) {
        d.createObjectStore('handles', { keyPath: 'id' })
        d.createObjectStore('snapshots', { keyPath: 'id' })
        d.createObjectStore('journal', { keyPath: 'path' })
        d.createObjectStore('versions', { keyPath: 'key' }).createIndex('byPath', 'pathKey')
        d.createObjectStore('themes', { keyPath: 'id' })
      },
    })
  }
  return dbPromise
}

/** A short random id, for handles, snapshots and tabs. */
export function randomId(bytes = 6): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('')
}
